<template>
  <div>
    <div class="form-input" :class="classNames" :data-format="suffixPrefix">
      <label
        v-if="showLabel"
        class="form-label"
        data-tool-tip="showInfoIcon"
        :for="name"
      >
        <span v-if="showTitle" class="+float-left">{{ title }}</span>
        <info-icon
          v-if="showInfoIcon"
          :title="title"
          :message="infoIconMsg"
        ></info-icon>
      </label>
      <input
        :readonly="readonly"
        :name="name"
        :value="value"
        :id="name"
        :maxlength="maxLength"
        v-on:focus="selectAll"
        v-on:keypress="preventBadInput"
        v-on:keyup="setValue($event.target.value)"
        v-on:change="updateValue($event.target.value)"
      />
    </div>
    <span
      class="error-message number-input__error"
      v-if="hasError || addError"
      >{{ error }}</span
    >
  </div>
</template>
<style scoped lang="scss">
/* style label to have specific height so that when title is hidden, alignment is maintained */
label {
  height: 27px;
}
</style>
<script>
import infoIcon from 'apps/bankrate-calculators/components/info-icon.vue'
import calculatorEventBus from 'apps/bankrate-calculators/events'

export default {
  components: {
    'info-icon': infoIcon
  },
  props: {
    name: {
      type: String,
      required: true
    },
    trackingName: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      required: true
    },
    showLabel: {
      type: Boolean,
      default: true
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    showInfoIcon: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    infoIconMsg: {
      type: String,
      default: ''
    },
    suffix: {
      type: String,
      default: ''
    },
    prefix: {
      type: String,
      default: ''
    },
    maxLength: {
      type: Number
    },
    initialValue: {
      type: Number
    },
    defaultValue: {
      type: Number
    },
    error: {
      type: String
    },
    minValue: {
      type: Number,
      default: 0
    },
    maxValue: {
      type: Number,
      default: 1000000
    },
    format: {
      type: String
    },
    synced: {
      type: Boolean,
      default: false
    },
    callback: {
      type: String,
      default: 'setNumberValue'
    },
    trackingEnabled: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      hasError: false
    }
  },
  computed: {
    value() {
      if (
        this.format &&
        typeof this.$options.filters[this.format] === 'function'
      ) {
        return this.$options.filters[this.format](this.$store.state[this.name])
      }
      return this.$store.state[this.name]
    },
    classNames() {
      let classes = this.suffix
        ? '--data-format-right'
        : this.prefix
        ? '--data-format-left'
        : ''
      classes += this.hasError || this.addError ? ' --has-error' : ''
      return classes
    },
    suffixPrefix() {
      return this.suffix ? this.suffix : this.prefix ? this.prefix : ''
    },
    fieldName() {
      return this.trackingName ? this.trackingName : this.title.toLowerCase()
    },
    addError() {
      return this.$store.state.errors[this.name]
    }
  },
  methods: {
    cleanValue(value) {
      if (value !== '') {
        value = value.replace(/[^0-9.]/g, '')
        if (value.match('.')) {
          return parseFloat(value)
        }
        return Number(value)
      }
    },
    preventBadInput(e) {
      if (
        (e.which < 48 || e.which > 57) && // digits
        e.which !== 44 &&
        e.which !== 46
      ) {
        // comma, period
        e.preventDefault()
      }
    },
    setValue(value) {
      this.hasError = false
      if (isNaN(value.replace(/[,]/g, ''))) {
        this.hasError = true
        this.$store.dispatch(this.callback, {
          name: this.name,
          value: value,
          error: this.hasError
        })
        return false
      } else {
        let cleanValue = this.cleanValue(value)
        const valueValid = this.isValid(cleanValue)
        if (typeof cleanValue === 'undefined' && value === '') {
          cleanValue = value
        }
        this.$store.dispatch(this.callback, {
          name: this.name,
          fieldName: this.fieldName,
          value: cleanValue,
          error: this.hasError
        })

        if (valueValid) {
          return cleanValue
        }
        return false
      }
    },
    updateValue(value) {
      if (value === '' && !isNaN(this.defaultValue)) {
        value = this.defaultValue.toString()
      }
      let cleanValue = this.setValue(value)
      let suffix = this.suffix
      if (suffix === 'years') {
        suffix = ' ' + suffix
      }

      if (this.trackingEnabled && false !== cleanValue) {
        cleanValue = this.prefix + String(cleanValue) + suffix

        calculatorEventBus.$emit('fieldChanged', {
          fieldName: this.fieldName,
          value: cleanValue
        })
      }
    },
    isValid(value) {
      value = parseFloat(value)
      if (
        value >= parseFloat(this.minValue) &&
        value <= parseFloat(this.maxValue)
      ) {
        this.hasError = false
        return true
      } else {
        this.hasError = true
        return false
      }
    },
    validateSyncedValue(value) {
      const cleanValue = this.cleanValue(String(value))
      this.hasError =
        isNaN(String(value).replace(',', '')) || !this.isValid(cleanValue)
    },
    selectAll(event) {
      // Workaround for Safari bug @todo - check if we still need this for new safari versions
      // http://stackoverflow.com/questions/1269722/selecting-text-on-focus-using-jquery-not-working-in-safari-and-chrome
      setTimeout(() => {
        event.target.select()
      }, 0)
    }
  },
  watch: {
    value(val) {
      if (this.synced) {
        this.validateSyncedValue(val)
      }
    }
  }
}
</script>
